import * as THREE from 'three';
import { TreeGenerator } from './TreeGenerator.js';
import { FoliageSystem } from './FoliageSystem.js';
import { CrystalFormation } from './CrystalFormation.js';
import { AnomalyGenerator } from './AnomalyGenerator.js';
import { GeyserSystem } from './GeyserSystem.js';
import { SurfacePlan } from './SurfacePlan.js';
import { pullMaterialTowardHue } from '../utils/palette.js';

/**
 * Central orchestrator for all planet surface scatter systems.
 *
 * Manages trees, foliage, crystals, anomalies, and geysers as a single
 * cohesive layer.  Each subsystem produces:
 *   1. A serialisable data array (for future multiplayer state sync)
 *   2. A THREE.Group of meshes
 *
 * Everything is seeded/deterministic — two clients given the same
 * planetData will produce identical surfaces.
 *
 * Usage:
 *   const scatter = new SurfaceScatter();
 *   const result  = scatter.generate(planetData, planetRadius, getHeightFn);
 *   planetGroup.add(result.group);
 *   // In game loop:
 *   scatter.update(dt, elapsedTime);
 *   // On planet leave:
 *   scatter.clear();
 */
export class SurfaceScatter {
  constructor() {
    this.trees    = new TreeGenerator();
    this.foliage  = new FoliageSystem();
    this.crystals = new CrystalFormation();
    this.anomalies = new AnomalyGenerator();
    this.geysers  = new GeyserSystem();

    this.group = new THREE.Group();
    this.group.name = 'surface-scatter';
  }

  /**
   * Generate all surface scatter for a planet.
   *
   * Order matters: landmarks (anomalies) claim their clearings first, then
   * resource fields (crystals, geysers) reserve their footprints, and flora
   * fills the open land that remains. All coordination goes through the
   * shared SurfacePlan so nothing overlaps.
   *
   * @param {object}      planetData   – planet config (seed, type, size, terrain …)
   * @param {number}      planetRadius – SURFACE_RADIUS × size
   * @param {function}    getHeightFn  – (normalizedDir: Vector3) => surfaceHeight
   * @param {SurfacePlan} [plan]       – shared zoning plan; created here if absent
   * @returns {{
   *   group: THREE.Group,
   *   treeData: object[],
   *   foliageData: object[],
   *   crystalData: object[],
   *   anomalyData: object[],
   *   geyserData: object[],
   * }}
   */
  generate(planetData, planetRadius, getHeightFn, plan = null) {
    this.clear();

    plan = plan || new SurfacePlan(planetData, planetRadius);

    const anomalyResult = this.anomalies.generate(planetData, planetRadius, getHeightFn, plan);
    const crystalResult = this.crystals.generate(planetData, planetRadius, getHeightFn, plan);
    const geyserResult  = this.geysers.generate(planetData, planetRadius, getHeightFn, plan);
    const treeResult    = this.trees.generate(planetData, planetRadius, getHeightFn, plan);
    const foliageResult = this.foliage.generate(planetData, planetRadius, getHeightFn, plan);

    this.group.add(treeResult.group);
    this.group.add(foliageResult.group);
    this.group.add(crystalResult.group);
    this.group.add(anomalyResult.group);
    this.group.add(geyserResult.group);

    // Pull every scatter material toward the planet's hue — mirrors the
    // terrain shader's shift so props share one palette instead of each
    // keeping its generic per-type color.
    if (planetData.visual?.hue != null) {
      const visited = new Set();
      this.group.traverse(obj => {
        if (!obj.material) return;
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach(m => pullMaterialTowardHue(m, planetData.visual.hue, visited));
      });
    }

    return {
      group:       this.group,
      treeData:    treeResult.trees,
      foliageData: foliageResult.foliage,
      crystalData: crystalResult.crystals,
      anomalyData: anomalyResult.anomalies,
      geyserData:  geyserResult.geysers,
    };
  }

  /**
   * Per-frame update for animated scatter (geysers, floating artifacts, etc.)
   * @param {number} dt   – delta time in seconds
   * @param {number} time – elapsed game time in seconds
   */
  update(dt, time) {
    this.geysers.update(dt, time);
    this.anomalies.update(dt, time);
  }

  /**
   * Get all crystal formation objects.
   */
  getCrystals() {
    return this.crystals.crystals;
  }

  /**
   * Get all anomaly data (for interaction system).
   */
  getAnomalies() {
    return this.anomalies.anomalies;
  }

  /**
   * Serialise the full surface state for network sync.
   * Only includes mutable state (health, interacted flags, etc.).
   */
  serialise() {
    return {
      crystals: this.crystals.crystals.map(c => ({
        id: c.resourceType, // or a unique ID
        health: c.health,
      })),
      anomalies: this.anomalies.anomalies.map(a => ({
        id: a.id,
        interacted: a.interacted,
      })),
    };
  }

  /**
   * Restore mutable state from a network sync payload.
   */
  deserialise(state) {
    if (state.crystals) {
      for (let i = 0; i < state.crystals.length && i < this.crystals.crystals.length; i++) {
        this.crystals.crystals[i].health = state.crystals[i].health;
      }
    }
    if (state.anomalies) {
      for (const sa of state.anomalies) {
        const local = this.anomalies.anomalies.find(a => a.id === sa.id);
        if (local) local.interacted = sa.interacted;
      }
    }
  }

  clear() {
    this.trees.clear();
    this.foliage.clear();
    this.crystals.clear();
    this.anomalies.clear();
    this.geysers.clear();

    while (this.group.children.length) {
      this.group.remove(this.group.children[0]);
    }
  }
}
